/**
 * Четыре канала вместо одного конверта (docs/ARCHITECTURE.md, 3.1).
 *
 * ПРАВИЛО, РАДИ КОТОРОГО ВСЁ ЭТО СДЕЛАНО: статика не ездит в потоке.
 *
 * У конкурента всё слито в один конверт `overlay:state` и уходит 9-11 раз в
 * секунду. Следствия замерены: блок настроек стрима 1 665 байт в КАЖДОМ
 * пакете, доля неизменяемой обвязки в простое от 45 до 85 % трафика, у
 * `horse-race` статика - более 80 % пакета
 * (docs/research/game-specs/_SUMMARY_CANVAS.md, 4.3).
 *
 * Разделение по каналам делает такую ошибку невыразимой: если поле не
 * меняется между тиками, оно принадлежит `session:config` или
 * `game:contract`, а не `game:state`. Механическая защита - тип NoStatic
 * в этом файле.
 */
export const CHANNELS = {
    /** Настройки стрима, виджеты, watermark, раскладка overlay, состояние лимита. */
    sessionConfig: 'session:config',
    /** Манифест игры, привязки действий, настройки HUD, коллекции. */
    gameContract: 'game:contract',
    /** Только изменившаяся динамика мира. */
    gameState: 'game:state',
    /** Справочник `id -> { имя, аватар, цвет }`. */
    directoryMembers: 'directory:members',
    /**
     * Поток событий зрителей: комментарии, подарки, лайки, подписки, репосты,
     * входы.
     *
     * Отдельный канал, а не часть `game:state`, по той же причине, по которой
     * разделены остальные: событие - это факт, случившийся один раз, а состояние
     * мира - величина, которая меняется. Смешав их, мы получили бы поток, в
     * котором каждый тик тащит историю событий, - ровно то, чем болеет
     * конкурент: у `chainsaw-clash` журнал столкновений занимает 52 % пикового
     * пакета и растёт квадратично (docs/ARCHITECTURE.md, 3.2).
     *
     * Что по нему НЕ едет: имя и аватар зрителя. Они уходят один раз каналом
     * `directory:members`, а здесь только идентификатор.
     */
    viewerEvent: 'viewer:event',
    /**
     * Команды действий: то, что сцена реально исполняет.
     *
     * Отдельный канал от `viewer:event` не ради стройности. Событие зрителя и
     * команда действия - разные вещи с разной судьбой: событие может не
     * породить ни одной команды (не подошло ни под одну привязку), может
     * породить несколько (одно событие - несколько действий), а команда может
     * прийти вообще без события - кнопкой «прогнать» из dashboard. Слив их в
     * один канал сделал бы прогон триггера особым случаем, а он обязан быть
     * обычным.
     *
     * Правила привязок по этому каналу НЕ ЕДУТ: сцена получает результат, а не
     * условия. Именно поэтому игровой чанк весит килобайты, а не мегабайты.
     */
    gameAction: 'game:action',
    /**
     * Прямой поток комментариев - для сцен, объявивших `chatAccess.direct`.
     *
     * Объявленное исключение из модели команд, а не обход её. Викторины и
     * словесные игры без него не работают: `guess-the-number` имеет ноль
     * привязываемых действий и живёт целиком на разборе комментариев.
     */
    gameChat: 'game:chat',
    /**
     * Запрос ресинхронизации: overlay -> сервер. Единственное сообщение канала
     * состояния, идущее в обратную сторону.
     *
     * Существует ради правила 1 раздела 3.2: «дельты вместо полного снимка,
     * полный снимок - только по запросу ресинхронизации». Без обратного запроса
     * это правило неисполнимо: overlay, потерявший пакет или подключившийся
     * посреди раунда, не может собрать мир из одних дельт, и единственным
     * выходом остаётся то, чем живёт конкурент, - слать полный снимок каждый
     * тик.
     *
     * Когда отправляется: при подключении сцены, после переподключения сокета и
     * при разрыве последовательности `simTick`. Ответ - кадр с полем `full`.
     */
    gameResync: 'game:resync',
    /**
     * Состав элементов сцены: overlay -> сервер.
     *
     * Второе и последнее сообщение, идущее в обратную сторону. Нужно редактору
     * раскладки: он двигает элементы по идентификаторам, а знать их может только
     * тот код, который их рисует. Список приезжает один раз после монтирования
     * сцены и меняется только вместе с версией игры.
     */
    gameLayoutSpec: 'game:layout-spec',
    /**
     * Команды ведущего игре: пауза, снятие паузы, перезапуск раунда.
     *
     * Третье и последнее направление протокола. Первые два - состояние мира к
     * зрителю и событие зрителя к игре; здесь ведущий обращается к миру. Путь
     * тот же, что у события зрителя: dashboard -> сервер -> мир. Прямо в overlay
     * команда не идёт никогда, потому что мир авторитетен на сервере.
     *
     * Какие команды принимает игра, объявляет её манифест: у викторины
     * перезапуск раунда значит одно, у арены другое, а игра, не умеющая паузу,
     * не должна получать кнопку.
     */
    directorCommand: 'director:command',
    /**
     * Сообщение ведущего зрителям в кадр.
     *
     * ОТДЕЛЬНО ОТ КОМАНД НАМЕРЕННО. Это не команда игре: игра о нём не знает,
     * рисует его overlay поверх сцены и снимает по истечении длительности.
     * Иначе каждая из десяти игр была бы обязана уметь показывать чужой текст.
     */
    directorMessage: 'director:message',
    /**
     * Звук оверлею: проиграть файл или оборвать всё.
     *
     * ОТДЕЛЬНЫЙ КАНАЛ, А НЕ `game:action`. Команда действия адресована СЦЕНЕ и
     * зависит от игры; звук к игре отношения не имеет - пад звучит и в паузе, и
     * между раундами, и когда сцена не выбрана вовсе. Смешав их, мы заставили
     * бы каждую из десяти игр знать про звук.
     */
    soundCommand: 'sound:command',
    /**
     * Сцена платформы: гифка, оповещение, эффект подарка, озвучка, музыка.
     *
     * Отдельно от `game:action` по той же причине, что и звук: это части
     * ПЛАТФОРМЫ, а не игры, и они живут поверх любой сцены и без неё.
     */
    stageCommand: 'stage:command',
    /**
     * Состояние сцены: overlay -> сервер -> консоль.
     *
     * Четвёртое обратное направление протокола. Положение трека и список
     * голосов знает только тот, кто их проигрывает.
     */
    stageState: 'stage:state',
    /**
     * Служебный канал: рукопожатие, отказы, присутствие overlay.
     *
     * Не входит в четвёрку ARCHITECTURE 3.1 намеренно: та четвёрка описывает
     * данные для отрисовки, а это транспортный уровень - кто подключился, с
     * какой версией и почему отказано. Смешивать его с данными нельзя ровно по
     * той же причине, по которой разделены остальные каналы.
     */
    system: 'system',
};
//# sourceMappingURL=channels.js.map